import React, { useState, useEffect } from "react";
import {
  Area,
  AreaChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import axios from "../../../utils/axios";

const DeviceChart = ({ id }) => {
  const [chartData, setChartData] = useState([]);
  const [flowUnit, setFlowUnit] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Function to fetch data using the optimized endpoint
  const fetchOptimizedData = async () => {
    setLoading(true);
    setError(null);

    try {
      console.log("Fetching data for device ID:", id);
      const response = await axios.post("flow/report/devices/chart24hour", {
        deviceId: id,
        numDataPoints: 100,
      });

      console.log("API Response:", response.data);

      // Store the flow unit from the API response
      const responseData = response?.data?.[0];
      setFlowUnit(responseData?.flow_unit || "");

      // Get results array safely
      const results = responseData?.data?.results || [];
      console.log("Results data:", results);

      if (results.length === 0) {
        console.warn("No data points returned from API");
      }

      // Create a more reliable data transformation
      const transformedData = results.map((item) => {
        // Make sure to handle all possible data formats
        return {
          // Keep date_time as string for now - simpler to debug
          date_time: item.date_time,
          // Get flowrate from any possible source
          flowrate: parseFloat(item.avg_flowrate || item.flowrate || 0),
          // Parse totalizer if available
          totalizer: parseFloat(item.end_totalizer || item.totalizer || 0),
        };
      });

      console.log("Transformed data:", transformedData);
      setChartData(transformedData);
    } catch (error) {
      console.error("Error fetching chart data:", error);
      setError(error.message || "Failed to fetch chart data");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (id) {
      fetchOptimizedData();
    }
  }, [id]);

  // Simple formatter for X-axis to keep dates readable
  const formatXAxis = (dateStr) => {
    if (!dateStr) return "";
    try {
      const date = new Date(dateStr);
      return date.toLocaleTimeString([], {
        hour: "2-digit",
        minute: "2-digit",
      });
    } catch (e) {
      console.error("Error formatting date:", e);
      return dateStr;
    }
  };

  if (loading) {
    return <div>Loading chart data...</div>;
  }

  if (error) {
    return <div>Error loading chart: {error}</div>;
  }

  if (!chartData || chartData.length === 0) {
    return <div>No data available for this time period</div>;
  }

  return (
    <div style={{ width: "100%", height: 400 }}>
      <ResponsiveContainer>
        <AreaChart
          data={chartData}
          margin={{
            top: 10,
            right: 30,
            left: 20,
            bottom: 30,
          }}
        >
          <CartesianGrid strokeDasharray="3 3" />
          {/* Simplified X-axis configuration */}
          <XAxis
            dataKey="date_time"
            tickFormatter={formatXAxis}
            tick={{
              angle: -45,
              textAnchor: "end",
              fontSize: 12,
            }}
            height={60}
          />
          <YAxis
            dataKey="flowrate"
            label={{
              value: `Flow Rate (${flowUnit})`,
              angle: -90,
              position: "insideLeft",
              style: { textAnchor: "middle" },
            }}
          />
          <Tooltip
            formatter={(value) => {
              if (value === null || value === undefined) return "0";
              if (typeof value === "number")
                return value.toFixed(2) + " " + flowUnit;
              return value;
            }}
          />
          <Area
            type="monotone"
            dataKey="flowrate"
            stroke="#09c4ff"
            fill="#09c4ff"
            name="Flow Rate"
          />
        </AreaChart>
      </ResponsiveContainer>
    </div>
  );
};

export default DeviceChart;
